import { X } from "lucide-react";

import { useCurrency } from "../../currency/CurrencyContext";
import type { Market } from "../../markets/api";
import { DEFAULT_FILTERS } from "../filters";
import type { ComparisonFilters } from "../types";

interface ActiveFilterChipsProps {
  filters: ComparisonFilters;
  markets: Market[];
  searchQuery: string;
  onFiltersChange: (filters: ComparisonFilters) => void;
  onSearchQueryChange: (value: string) => void;
}

interface FilterChip {
  id: string;
  label: string;
  onRemove: () => void;
}

function sameMarkets(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

export function ActiveFilterChips({
  filters,
  markets,
  searchQuery,
  onFiltersChange,
  onSearchQueryChange,
}: ActiveFilterChipsProps) {
  const { currency, toCurrency } = useCurrency();
  const marketNames = new Map(markets.map((market) => [market.code, market.name]));
  const formatNumber = (value: number) =>
    new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(value);
  const formatPrice = (value: number) =>
    `${formatNumber(toCurrency(value))} ${currency.symbol}`;
  const labelMarkets = (marketCodes: string[]) =>
    marketCodes.map((code) => marketNames.get(code) ?? code).join(", ");
  const reset = (patch: Partial<ComparisonFilters>) =>
    onFiltersChange({ ...filters, ...patch });
  const chips: FilterChip[] = [];

  if (searchQuery.trim()) {
    chips.push({
      id: "search",
      label: searchQuery.trim(),
      onRemove: () => onSearchQueryChange(""),
    });
  }
  if (!sameMarkets(filters.buyMarkets, DEFAULT_FILTERS.buyMarkets)) {
    chips.push({
      id: "buy-markets",
      label: `Покупка: ${labelMarkets(filters.buyMarkets)}`,
      onRemove: () => reset({ buyMarkets: [...DEFAULT_FILTERS.buyMarkets] }),
    });
  }
  if (!sameMarkets(filters.sellMarkets, DEFAULT_FILTERS.sellMarkets)) {
    chips.push({
      id: "sell-markets",
      label: `Продажа: ${labelMarkets(filters.sellMarkets)}`,
      onRemove: () => reset({ sellMarkets: [...DEFAULT_FILTERS.sellMarkets] }),
    });
  }
  const addRangeChips = (
    id: string,
    label: string,
    minimum: number | null,
    maximum: number | null,
    clearMinimum: () => void,
    clearMaximum: () => void,
    format = formatNumber,
  ) => {
    if (minimum !== null) {
      chips.push({ id: `${id}-min`, label: `${label}: от ${format(minimum)}`, onRemove: clearMinimum });
    }
    if (maximum !== null) {
      chips.push({ id: `${id}-max`, label: `${label}: до ${format(maximum)}`, onRemove: clearMaximum });
    }
  };

  addRangeChips(
    "buy-price",
    "Цена покупки",
    filters.minBuyPrice,
    filters.maxBuyPrice,
    () => reset({ minBuyPrice: null }),
    () => reset({ maxBuyPrice: null }),
    formatPrice,
  );
  addRangeChips(
    "buy-orders",
    "Ордера покупки",
    filters.minBuyOrders,
    filters.maxBuyOrders,
    () => reset({ minBuyOrders: null }),
    () => reset({ maxBuyOrders: null }),
  );
  addRangeChips(
    "sell-orders",
    "Ордера продажи",
    filters.minSellOrders,
    filters.maxSellOrders,
    () => reset({ minSellOrders: null }),
    () => reset({ maxSellOrders: null }),
  );

  const priceFilterLabels = {
    current: "Цена продажи",
    median: `Медиана · ${filters.medianWindowDays} дн.`,
    average: `Средняя · ${filters.medianWindowDays} дн.`,
  } as const;
  const priceRange =
    filters.priceFilterMode === "median"
      ? [filters.minMedianPrice, filters.maxMedianPrice, "minMedianPrice", "maxMedianPrice"] as const
      : filters.priceFilterMode === "average"
        ? [filters.minAveragePrice, filters.maxAveragePrice, "minAveragePrice", "maxAveragePrice"] as const
        : [filters.minSellPrice, filters.maxSellPrice, "minSellPrice", "maxSellPrice"] as const;
  addRangeChips(
    `sell-price-${filters.priceFilterMode}`,
    priceFilterLabels[filters.priceFilterMode],
    priceRange[0],
    priceRange[1],
    () => reset({ [priceRange[2]]: null }),
    () => reset({ [priceRange[3]]: null }),
    formatPrice,
  );
  if (filters.minDailySales !== null) {
    chips.push({
      id: "daily-sales",
      label: `Продаж в сутки: от ${formatNumber(filters.minDailySales)}`,
      onRemove: () => reset({ minDailySales: null }),
    });
  }
  if (
    filters.priceFilterMode === "median" &&
    filters.minMedianDailySales !== null
  ) {
    chips.push({
      id: "median-daily-sales",
      label: `Медиана продаж: от ${formatNumber(filters.minMedianDailySales)}`,
      onRemove: () => reset({ minMedianDailySales: null }),
    });
  }
  if (filters.minActiveDaysPct !== null) {
    chips.push({
      id: "active-days",
      label: `Активные дни: от ${formatNumber(filters.minActiveDaysPct)}%`,
      onRemove: () => reset({ minActiveDaysPct: null }),
    });
  }
  if (
    filters.priceFilterMode === "median" &&
    filters.medianWeighted !== DEFAULT_FILTERS.medianWeighted &&
    (filters.minMedianPrice !== null || filters.maxMedianPrice !== null)
  ) {
    chips.push({
      id: "median-weighting",
      label: "Медиана без веса продаж",
      onRemove: () => reset({ medianWeighted: DEFAULT_FILTERS.medianWeighted }),
    });
  }

  addRangeChips(
    "roi",
    "ROI",
    filters.minProfitPct,
    filters.maxProfitPct,
    () => reset({ minProfitPct: null }),
    () => reset({ maxProfitPct: null }),
    (value) => `${formatNumber(value)}%`,
  );
  if (filters.maxExpectedSaleDays !== null) {
    chips.push({
      id: "expected-exit",
      label: `Выход: до ${formatNumber(filters.maxExpectedSaleDays)} дн.`,
      onRemove: () => reset({ maxExpectedSaleDays: null }),
    });
  }
  if (filters.minSafeVolumeProfit !== null) {
    chips.push({
      id: "safe-volume-profit",
      label: `Прибыль объёма: от ${formatPrice(filters.minSafeVolumeProfit)}`,
      onRemove: () => reset({ minSafeVolumeProfit: null }),
    });
  }
  if (filters.hideNegativeDeals) {
    chips.push({
      id: "hide-negative",
      label: "Без отрицательных сделок",
      onRemove: () => reset({ hideNegativeDeals: false }),
    });
  }
  if (filters.hideStickers) {
    chips.push({
      id: "hide-stickers",
      label: "Без стикеров",
      onRemove: () => reset({ hideStickers: false }),
    });
  }

  if (chips.length === 0) {
    return <div className="active-filter-placeholder">Фильтры не заданы</div>;
  }

  return (
    <div className="active-filter-chips" aria-label="Активные фильтры">
      {chips.map((chip) => (
        <span className="active-filter-chip" key={chip.id}>
          <span>{chip.label}</span>
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={`Убрать фильтр: ${chip.label}`}
            title="Убрать фильтр"
          >
            <X aria-hidden="true" />
          </button>
        </span>
      ))}
    </div>
  );
}
